import { useState } from "react";
import { Upload, Loader2, CheckCircle2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";

const LABELS = {
  date: "Date", commercial: "Commercial", plate: "Immatriculation", mileage: "Kilométrage", station: "Station",
  card_number: "Numéro de carte", holder: "Titulaire", user: "Commercial", user_name: "Commercial", friday_date: "Vendredi concerné",
  perso_km: "Km personnels", existing: "Valeur existante", source: "Source", period_label: "Période",
  fuel: "Carburant", liters: "Litres", price_per_liter: "Prix/L", amount_ht: "HT", amount_ttc: "TTC", vat: "TVA",
  reference: "Référence", type: "Type", email: "Email", comment: "Commentaire", brand: "Marque", model: "Modèle",
  engine: "Motorisation", first_registration: "Mise en circulation", status: "Statut", financing: "Financement",
  contract_km: "Km contractuel", contract_end: "Fin de contrat", commercial_name: "Porteur (fichier)", user_name: "Rapproché à",
  name: "Nom", action: "Action", can_view_history: "Historique", generated: "Mot de passe provisoire", start_date: "Début",
  end_date: "Fin", password: "Mot de passe", active: "Actif", history: "Historique autorisé", start: "Début", end: "Fin",
  an_status: "Statut anomalie", next_due_date: "Prochaine échéance", next_due_km: "Prochain km", next_date: "Prochaine échéance date",
  next_km: "Prochaine échéance km", cost: "Coût TTC", provider: "Prestataire", voyants: "Voyants", freins: "Freins", pneus: "Pneus", dommages: "Dommages",
};
const isoToFr = (v) => (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v.split("-").reverse().join("/") : v);

export default function ImportWidget({ endpoint, params = {}, title, hint, previewCols, onDone, testid }) {
  const [file, setFile] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  const send = async (dry) => {
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, fd, { params: { ...params, dry_run: dry } });
      setRes(data);
      if (!dry) {
        toast.success(`${data.valid} ligne(s) importée(s)`);
        setFile(null);
        onDone?.();
      }
    } catch (e) {
      toast.error(errMsg(e));
      setRes(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 rise" data-testid={testid}>
      <div className="flex items-start gap-3 mb-4">
        <FileSpreadsheet className="w-5 h-5 text-[#F49619] mt-0.5" />
        <div>
          <h3 className="font-semibold text-[#0E2D43]">{title}</h3>
          <p className="text-xs text-slate-500 mt-0.5">{hint}</p>
        </div>
      </div>
      <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-lg py-8 cursor-pointer hover:border-[#F49619] hover:bg-[#FFF8EE] transition-colors">
        <Upload className="w-6 h-6 text-slate-400" />
        <span className="text-sm font-semibold text-[#0E2D43]">{file ? file.name : "Choisir un fichier CSV ou Excel"}</span>
        <input type="file" accept=".csv,.xlsx,.xls,.txt" className="hidden" data-testid={`${testid}-file-input`}
          onChange={(e) => { setFile(e.target.files[0] || null); setRes(null); e.target.value = ""; }} />
      </label>
      <div className="flex gap-2 mt-4">
        <Button variant="outline" disabled={!file || busy} onClick={() => send(true)} data-testid={`${testid}-preview-btn`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Prévisualiser"}
        </Button>
        {res?.dry_run && res.valid > 0 && (
          <Button disabled={busy} onClick={() => send(false)} className="bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold" data-testid={`${testid}-confirm-btn`}>
            Importer {res.valid} ligne(s)
          </Button>
        )}
      </div>
      {res && <Result res={res} cols={previewCols} testid={testid} />}
    </div>
  );
}

function Result({ res, cols: colsProp, testid }) {
  const cols = colsProp || Object.keys(res.preview[0] || {}).filter((k) => !/^(id|.*_id|is_demo|created_at|import_id|photos)$/.test(k));
  return (
    <div className="mt-5 space-y-3" data-testid={`${testid}-result`}>
      <div className="flex flex-wrap gap-2 text-xs font-semibold">
        <span className="px-2.5 py-1 rounded-full bg-slate-100">{res.total_rows} lignes lues</span>
        <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">{res.valid} valides</span>
        {res.duplicates > 0 && <span className="px-2.5 py-1 rounded-full bg-amber-50 text-amber-700">{res.duplicates} doublons ignorés</span>}
        {res.unmatched > 0 && <span className="px-2.5 py-1 rounded-full bg-rose-50 text-rose-700">{res.unmatched} non rapprochées</span>}
        {!res.dry_run && <span className="px-2.5 py-1 rounded-full bg-[#0E2D43] text-white inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Import terminé</span>}
        {!res.dry_run && (res.created > 0 || res.updated > 0) && <span className="px-2.5 py-1 rounded-full bg-slate-100">{res.created} créé(s) · {res.updated} mis à jour</span>}
        {res.extra?.anomalies > 0 && <span className="px-2.5 py-1 rounded-full bg-rose-50 text-rose-700">{res.extra.anomalies} anomalie(s) {res.dry_run ? "seront créées" : "créées"}</span>}
      </div>
      {Array.isArray(res.extra) && res.extra.length > 0 && (
        <div className="text-xs bg-[#FEF3E2] border border-[#F49619]/40 rounded-md p-3" data-testid={`${testid}-passwords`}>
          <b>Mots de passe provisoires à transmettre (affichés une seule fois) :</b>
          <ul className="mt-1 font-mono">{res.extra.map((p) => <li key={p.email}>{p.email} — {p.password}</li>)}</ul>
        </div>
      )}
      <div className="text-xs text-slate-500">
        Colonnes reconnues : {Object.entries(res.mapping).map(([k, v]) => `${LABELS[k] || k} ← « ${v} »`).join(" · ")}
      </div>
      {res.errors.length > 0 && (
        <ul className="text-xs text-rose-700 bg-rose-50 rounded-md p-3 max-h-28 overflow-y-auto">{res.errors.map((e) => <li key={e}>{e}</li>)}</ul>
      )}
      {res.preview.length > 0 && (
        <div className="overflow-x-auto border rounded-lg">
          <table className="tbl w-full">
            <thead><tr>{cols.map((c) => <th key={c}>{LABELS[c] || c}</th>)}</tr></thead>
            <tbody>{res.preview.map((r, i) => <tr key={i}>{cols.map((c) => <td key={c}>{isoToFr(r[c]) ?? "—"}</td>)}</tr>)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
